import React, { useState, useEffect } from 'react';
import { X, CheckCircle2, Clock, Check } from 'lucide-react';
import homeworkService from '../../services/HomeworkService.js';

const AssignmentSubmissionsModal = ({
  show,
  onClose,
  assignment
}) => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);

  const fetchSubmissions = async () => {
    if (!assignment) return;
    try {
      setLoading(true);
      const res = await homeworkService.getSubmissions(assignment.id);
      if (res && res.success && res.allSubmissions) {
        setSubmissions(res.allSubmissions);
      } else {
        setSubmissions([]);
      }
    } catch (err) {
      console.error("Error fetching submissions:", err);
      setSubmissions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show && assignment) {
      fetchSubmissions();
    }
  }, [show, assignment]);

  if (!show || !assignment) return null;

  const handleMarkComplete = async (studentId) => {
    try {
      setUpdatingId(studentId);
      const res = await homeworkService.makeRequest(`/api/teacher/homework/${assignment.id}/student/${studentId}/complete`, {
        method: 'PUT'
      });
      if (res && res.success) {
        alert('Student submission marked as Completed!');
        fetchSubmissions();
      } else {
        alert('Error completing submission: ' + (res.error || 'Request failed'));
      }
    } catch (e) {
      console.error(e);
      alert('Error updating status: ' + e.message);
    } finally {
      setUpdatingId(null);
    }
  };

  const submittedCount = submissions.filter(s => s.status !== 'PENDING').length;
  const pendingCount = submissions.length - submittedCount;

  return (
    <div className="fs-modal-overlay" style={{ zIndex: 1200 }}>
      <div className="fs-modal-card" style={{ maxWidth: '640px', borderRadius: '16px', overflow: 'hidden' }}>
        
        {/* HEADER */}
        <div style={{
          padding: '18px 24px',
          borderBottom: '1px solid #E2E8F0',
          display: 'flex',
          justify: 'space-between',
          alignItems: 'center',
          backgroundColor: '#F8FAFC'
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#0F172A' }}>
              {assignment.title}
            </h3>
            <div style={{ fontSize: '12.5px', color: '#2563EB', fontWeight: '600', marginTop: '2px' }}>
              {assignment.className} Section {assignment.section} • {assignment.subject} ({assignment.teacher})
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748B' }}>
            <X size={20} />
          </button>
        </div>

        {/* BODY */}
        <div style={{ padding: '20px 24px' }}>
          
          {/* KPI Summary Banner */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '10px',
            backgroundColor: '#EFF6FF',
            border: '1px solid #BFDBFE',
            borderRadius: '10px',
            padding: '12px 14px',
            marginBottom: '18px'
          }}>
            <div>
              <div style={{ fontSize: '11px', color: '#1E40AF', fontWeight: '700' }}>TOTAL</div>
              <div style={{ fontSize: '16px', fontWeight: '800', color: '#1E3A8A' }}>{submissions.length} Students</div>
            </div>
            <div>
              <div style={{ fontSize: '11px', color: '#166534', fontWeight: '700' }}>SUBMITTED</div>
              <div style={{ fontSize: '16px', fontWeight: '800', color: '#15803D' }}>{submittedCount}</div>
            </div>
            <div>
              <div style={{ fontSize: '11px', color: '#B45309', fontWeight: '700' }}>PENDING</div>
              <div style={{ fontSize: '16px', fontWeight: '800', color: '#D97706' }}>{pendingCount}</div>
            </div>
            <div>
              <div style={{ fontSize: '11px', color: '#64748B', fontWeight: '700' }}>DUE DATE</div>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#334155', marginTop: '2px' }}>{assignment.dueDate}</div>
            </div>
          </div>

          {/* Student Submissions Table */}
          <div style={{ maxHeight: '350px', overflowY: 'auto', border: '1px solid #E2E8F0', borderRadius: '10px' }}>
            {loading ? (
              <div style={{ padding: '24px', textAlign: 'center', color: '#64748B', fontSize: '13.5px' }}>
                Loading submissions from database...
              </div>
            ) : submissions.length > 0 ? (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#F8FAFC', borderBottom: '1px solid #E2E8F0', textAlign: 'left', color: '#64748B' }}>
                    <th style={{ padding: '10px 14px' }}>Student</th>
                    <th style={{ padding: '10px 14px' }}>Status</th>
                    <th style={{ padding: '10px 14px' }}>Submitted Date</th>
                    <th style={{ padding: '10px 14px', textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {submissions.map((s, idx) => {
                    const isSubmitted = s.status !== 'PENDING';
                    return (
                      <tr key={s.studentId} style={{ borderBottom: idx < submissions.length - 1 ? '1px solid #F1F5F9' : 'none' }}>
                        <td style={{ padding: '10px 14px', fontWeight: '600', color: '#0F172A' }}>
                          {s.studentName} <span style={{ fontSize: '11px', color: '#94A3B8', fontWeight: '500' }}>({s.studentId})</span>
                        </td>
                        <td style={{ padding: '10px 14px' }}>
                          {isSubmitted ? (
                            <span style={{ backgroundColor: '#DCFCE7', color: '#166534', padding: '3px 8px', borderRadius: '12px', fontSize: '11.5px', fontWeight: '700', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                              <CheckCircle2 size={12} /> {s.status}
                            </span>
                          ) : (
                            <span style={{ backgroundColor: '#FEF3C7', color: '#92400E', padding: '3px 8px', borderRadius: '12px', fontSize: '11.5px', fontWeight: '700', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                              <Clock size={12} /> Pending
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '10px 14px', color: '#64748B' }}>
                          {s.submittedAt ? new Date(s.submittedAt).toLocaleDateString() : '-'}
                        </td>
                        <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                          {!isSubmitted ? (
                            <button
                              onClick={() => handleMarkComplete(s.studentId)}
                              disabled={updatingId === s.studentId}
                              style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', color: '#166534', fontWeight: '700', fontSize: '12px', cursor: 'pointer', padding: '4px 10px', borderRadius: '6px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                            >
                              <Check size={12} /> Mark Completed
                            </button>
                          ) : (
                            <span style={{ fontSize: '12px', color: '#64748B', fontWeight: '600' }}>Verified</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div style={{ padding: '24px', textAlign: 'center', color: '#64748B', fontSize: '13.5px' }}>
                No student enrollments found in this class.
              </div>
            )}
          </div>

        </div>

        {/* FOOTER */}
        <div style={{
          padding: '14px 24px',
          borderTop: '1px solid #E2E8F0',
          display: 'flex',
          justify: 'flex-end',
          backgroundColor: '#F8FAFC'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 18px',
              borderRadius: '8px',
              border: '1px solid #CBD5E1',
              backgroundColor: 'white',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Close Panel
          </button>
        </div>

      </div>
    </div>
  );
};

export default AssignmentSubmissionsModal;
